<script setup lang="ts">
import { Loader2, Pencil } from 'lucide-vue-next';
import { ref, watch } from 'vue';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { Member } from '@/stores/access-management';

interface Props {
  member: Member;
  orgId: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  roleUpdated: [member: Member, newRole: string];
}>();

const isOpen = ref(false);
const isUpdating = ref(false);
const selectedRole = ref(props.member.role);

watch(isOpen, (open) => {
  if (open) {
    // Reset to current role when dialog opens
    selectedRole.value = props.member.role;
  }
});

async function handleSave() {
  if (selectedRole.value !== props.member.role) {
    isUpdating.value = true;
    try {
      emit('roleUpdated', props.member, selectedRole.value);
      await new Promise((resolve) => setTimeout(resolve, 300));
      isOpen.value = false;
    } finally {
      isUpdating.value = false;
    }
  } else {
    isOpen.value = false;
  }
}
</script>

<template>
  <Dialog v-model:open="isOpen">
    <DialogTrigger as-child>
      <slot name="trigger">
        <Button variant="ghost" size="sm">
          <Pencil class="h-4 w-4 mr-2" />
          Edit Role
        </Button>
      </slot>
    </DialogTrigger>

    <DialogContent class="sm:max-w-[425px]">
      <DialogHeader>
        <DialogTitle>Edit Member Role</DialogTitle>
        <DialogDescription>
          Change the role for {{ member.email }}
        </DialogDescription>
      </DialogHeader>

      <div class="grid gap-4 py-4">
        <div class="grid gap-2">
          <Label for="role">Role</Label>
          <Select v-model="selectedRole">
            <SelectTrigger id="role">
              <SelectValue placeholder="Select role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="OWNER">
                <div class="flex flex-col">
                  <span class="font-medium">Owner</span>
                  <span class="text-muted-foreground text-xs">Full control over organization</span>
                </div>
              </SelectItem>
              <SelectItem value="ADMIN">
                <div class="flex flex-col">
                  <span class="font-medium">Admin</span>
                  <span class="text-muted-foreground text-xs">Can manage members and settings</span>
                </div>
              </SelectItem>
              <SelectItem value="MEMBER">
                <div class="flex flex-col">
                  <span class="font-medium">Member</span>
                  <span class="text-muted-foreground text-xs">Full access to organization data</span>
                </div>
              </SelectItem>
              <SelectItem value="VIEWER">
                <div class="flex flex-col">
                  <span class="font-medium">Viewer</span>
                  <span class="text-muted-foreground text-xs">Read-only access</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" @click="isOpen = false" :disabled="isUpdating">
          Cancel
        </Button>
        <Button @click="handleSave" :disabled="isUpdating">
          <Loader2 v-if="isUpdating" class="h-4 w-4 mr-2 animate-spin" />
          Save Changes
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
